import _ from 'lodash';

import {RunsDataQuery} from '../../../containers/RunsDataLoader';
import {getDatadogRum} from '../../../util/datadog/datadogRum';
import {MULTI_METRIC_MAX_RUNS} from '../defaults';
import {BucketedLine} from '../types';
import {SingleQuery} from './../../../util/queryTypes';
import {
  BackendLinesIdQueryBehavior,
  BackendLinesQueryBehavior,
  BucketedQueryBehavior,
  BucketedQueryWithInternalIdBehavior,
  GroupedBackendLinesQueryBehavior,
  GroupedBucketedQueryBehavior,
  GroupedBucketedQueryWithInternalIdBehavior,
  ShadowedBackendLinesQueryBehavior,
} from './queries/behaviors';
import {BucketedQueryType, QueryVars, queryVars} from './queries/queryVars';
import {BaseQueryBehavior} from './queries/types';
import {LoggingInfo} from './useLoggingInfo';

const DD_RUM = getDatadogRum();

async function executeQueryBehavior<
  TRes,
  TRuns extends {__typename: string; totalCount?: number},
>(
  behavior: BaseQueryBehavior<TRes, TRuns>,
  vars: QueryVars,
  runIds: string[],
  lastUpdated: string,
  currentRunsById: Record<string, BucketedLine>
) {
  const oldLimit = vars.limit;
  let newLimit = oldLimit;

  if (vars.bucketedHistorySpecs.length > 1) {
    newLimit = Math.max(
      1,
      Math.floor(MULTI_METRIC_MAX_RUNS / vars.bucketedHistorySpecs.length)
    );
    // don't let multi-metrics ever query for more runs than the single metric limit
    vars.limit = Math.min(newLimit, oldLimit);
  }

  const absoluteStartTime = Date.now();
  const t0 = performance.now();
  const result = await behavior.query(vars, runIds, lastUpdated);
  const t1 = performance.now();

  const logData = {
    behavior: behavior.query.name,
    bins: vars.bins,
    isInitialQuery: lastUpdated === new Date(0).toISOString(),
    isMultiMetrics: vars.bucketedHistorySpecs.length > 1,
    isSystemMetrics: vars.isSystemMetrics,
    isThrottledMultiMetrics: false,
    lastUpdated,
    maxGroups: vars.maxGroups,
    newLimit,
    newLoad: newLimit * vars.bucketedHistorySpecs.length,
    numMetrics: vars.bucketedHistorySpecs.length,
    numSpecs: vars.bucketedHistorySpecs.length,
    oldLoad: oldLimit * vars.bucketedHistorySpecs.length,
    oldLimit,
    order: vars.order,
  };
  /**
   * APM traces in Datadog are hard to segment when the query args are JSON strings (like `bucketedHistorySpecs`). For analysis it's easier to compute certain kinds of information in the client and track it through a RUM action.
   *
   * Here we're primarily interested in tracking frequency and performance of multi-metric panels which is hard to slice in Datadog.
   */
  DD_RUM?.addDurationVital('LinePlotQueryProfile', {
    description: 'Measures the performance of the line plot query',
    context: logData,
    startTime: absoluteStartTime,
    duration: t1 - t0,
  });

  const runs = behavior.unpack(result);

  if (behavior.postProcess) {
    behavior.postProcess(runs);
  }
  const runsById = behavior.applyDeltaFn({
    currentRunsById,
    runs,
    specs: vars.bucketedHistorySpecs,
  });
  return {
    runsById,
    isAggregated: runs?.__typename === 'GroupedRunConnection',
    totalRuns: runs?.totalCount,
  };
}

export async function bucketedQuery({
  currentRunsById,
  expressions,
  nBuckets,
  queryType,
  runsDataQuery,
  singleQuery,
  loggingInfo,
}: {
  currentRunsById: Record<string, BucketedLine>;
  expressions: string[] | undefined;
  nBuckets: number;
  queryType: BucketedQueryType;
  runsDataQuery: RunsDataQuery;
  singleQuery: SingleQuery;
  loggingInfo: LoggingInfo;
}) {
  const runIds = Object.keys(currentRunsById);
  const lastUpdated =
    _.max(Object.values(currentRunsById).map(l => l.updatedAt)) ??
    new Date(0).toISOString();
  const vars = queryVars(
    runsDataQuery,
    singleQuery,
    nBuckets,
    queryType,
    expressions,
    'Run History Line Plot'
  );

  DD_RUM?.addAction(`Dispatching ${queryType} delta query`, {
    ...vars,
    ...loggingInfo,
  });

  // This is a switch statement because Typescript is too dumb to infer the type of the queryBehavior properly, even though
  // everything else is the same.
  // You'd think that this would work:
  //
  // const queryBehaviorsByType = {
  //   BucketedQuery: BucketedQueryBehavior,
  //   BucketedQueryWithInternalId: BucketedQueryWithInternalIdBehavior,
  //   GroupedBucketedQuery: GroupedBucketedQueryBehavior,
  //   GroupedBucketedQueryWithInternalId: GroupedBucketedQueryWithInternalIdBehavior,
  // }
  // const queryBehavior = queryBehaviorsByType[queryType];
  // return await executeQueryBehavior(queryBehavior, vars, runIds, lastUpdated, currentRunsById);
  //
  // But it doesn't, even though it's functionally equivalent.
  switch (queryType) {
    case 'ShadowedBackendLinesQuery':
      return await executeQueryBehavior(
        ShadowedBackendLinesQueryBehavior,
        vars,
        runIds,
        lastUpdated,
        currentRunsById
      );
    case 'BucketedBackendLinesQuery':
      return await executeQueryBehavior(
        BackendLinesQueryBehavior,
        vars,
        runIds,
        lastUpdated,
        currentRunsById
      );
    case 'BucketedBackendLinesQueryId':
      return await executeQueryBehavior(
        BackendLinesIdQueryBehavior,
        vars,
        runIds,
        lastUpdated,
        currentRunsById
      );
    case 'BucketedQuery':
      return await executeQueryBehavior(
        BucketedQueryBehavior,
        vars,
        runIds,
        lastUpdated,
        currentRunsById
      );
    case 'BucketedQueryId':
      return await executeQueryBehavior(
        BucketedQueryWithInternalIdBehavior,
        vars,
        runIds,
        lastUpdated,
        currentRunsById
      );
    case 'GroupedBucketedQuery':
      return await executeQueryBehavior(
        GroupedBucketedQueryBehavior,
        vars,
        runIds,
        lastUpdated,
        currentRunsById
      );
    case 'GroupedBucketedQueryId':
      return await executeQueryBehavior(
        GroupedBucketedQueryWithInternalIdBehavior,
        vars,
        runIds,
        lastUpdated,
        currentRunsById
      );
    case 'GroupedBucketedBackendLinesQuery':
      return await executeQueryBehavior(
        GroupedBackendLinesQueryBehavior,
        vars,
        runIds,
        lastUpdated,
        currentRunsById
      );
  }
}
